import React, { useState } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GoogleOAuthButton = ({ service, serviceName, icon, description, connected, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Get authorization URL from backend
      const response = await axios.get(
        `${API_BASE_URL}/api/oauth/google/start/${service}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { authorization_url } = response.data;
      
      // Open OAuth consent in new window
      const width = 600;
      const height = 700;
      const left = (window.screen.width / 2) - (width / 2);
      const top = (window.screen.height / 2) - (height / 2);
      
      const popup = window.open(
        authorization_url,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
      );
      
      // Poll for popup close or check for callback
      const checkPopup = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setLoading(false);
          // Check status after popup closes
          setTimeout(() => {
            checkOAuthStatus();
          }, 1000);
        }
      }, 500);
      
    } catch (error) {
      console.error('OAuth error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start OAuth flow');
      setLoading(false);
    }
  };

  const checkOAuthStatus = async () => {
    setChecking(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/oauth/google/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const serviceStatus = response.data.status[service];
      if (serviceStatus && serviceStatus.connected && !serviceStatus.expired) {
        toast.success(`${serviceName} connected successfully!`);
        if (onStatusChange) {
          onStatusChange(service, true);
        }
      }
    } catch (error) {
      console.error('Error checking OAuth status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Are you sure you want to disconnect ${serviceName}?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(
        `${API_BASE_URL}/api/oauth/google/disconnect/${service}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${serviceName} disconnected`);
      if (onStatusChange) {
        onStatusChange(service, false);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-1">{icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">{serviceName}</h3>
            <p className="text-sm text-gray-600 mb-3">{description}</p>
            {connected && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">Connected</span>
              </div>
            )}
          </div>
        </div>
        <div>
          {checking ? (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          ) : connected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={loading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {loading ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={handleConnect}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleOAuthButton;
